import { useCallback, useMemo, useState } from 'react';
import { Anchor, Avatar, Box, Flex, Group, Menu, Text } from '@mantine/core';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../routes/paths';
import { RootState } from '../../../app/RootState';
import { logOut } from '../../../app/slices/authSlice';
import { setLoginAllRoles, setLoginRole } from '../../../app/slices/companySlice';
import { useAppDispatch } from '../../../app/store';
import { clickableColor } from '../../../components/mantineReactTable/sizes';
import DisplayIcon from '../../../components/iconSelector/DispalyIcon';
import classes from '../../../Global.module.css';
import { apiURL } from '../../../url/url';
import { roleLevels } from '../../../constants/Constant';
import type { UsersRoles } from '../../../types/users';
// import { clearMaintenanceState, readMaintenanceState, writeMaintenanceState } from '../../maintenance/maintenance';

interface Props {
  isEnableIconOnly?: boolean;
  fullName?: string;
  canAccessMyProfile?: boolean;
  roleDefinitions?: UsersRoles[];
}

const normalizeRoleName = (role: string) => role.replace(/\s+/g, '').toLowerCase();
const rolePriorities = new Map(
  Object.entries(roleLevels).map(([role, priority]) => [normalizeRoleName(role), priority]),
);

const UserMenu = ({ isEnableIconOnly, fullName, roleDefinitions = [] }: Props) => {
  const loginCurrentRole = useSelector((state: RootState) => state.company.loginRole) || sessionStorage.getItem('Role');
  const navigate = useNavigate();
  const reduxRoles = useSelector((state: RootState) => state.company?.loginRoles);

  const getStoredRoles = (): string[] => {
    try {
      const stored = sessionStorage.getItem('AllRoles');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const normalizedRoles: string[] = Array.isArray(reduxRoles) && reduxRoles?.length > 0 ? reduxRoles : getStoredRoles();

  const switchRole = (role: string | null) => {
    if (!role) return;
    dispatch(setLoginRole(role));
    sessionStorage.setItem('Role', role);
    navigate(ROUTES.home);
  };
  const dispatch = useAppDispatch();
  const UserEmail = sessionStorage.getItem('LoggedUserEmail');
  const firstLetter = UserEmail ? UserEmail.charAt(0).toUpperCase() : '';

  const logoutuser = useCallback(() => {
    const loginType = sessionStorage.getItem('loginType');
    if (loginType === 'Identity') {
      dispatch(setLoginRole(''));
      dispatch(logOut());
      sessionStorage.clear();
      dispatch(setLoginAllRoles([]));
      localStorage.removeItem('redirectAfterLogin');
      navigate(ROUTES.login);
    } else {
      const email = sessionStorage.getItem('LoggedUserEmail') ?? '';
      sessionStorage.clear();
      window.location.href = `${apiURL}/identity/azuread/logout?email=${encodeURIComponent(email)}`;
    }
  }, [dispatch, navigate]);

  const [hoveredRole, setHoveredRole] = useState<string | null>(null);
  // const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);
  // const [maintenanceMinutes, setMaintenanceMinutes] = useState<number>(60);
  // const [maintenanceReason, setMaintenanceReason] = useState('');
  // const [isSubmitting, setIsSubmitting] = useState(false);

  // const normalizedCurrentRole = String(loginCurrentRole || '').replace(/\s+/g, '').toLowerCase();
  // const canManageMaintenance = normalizedCurrentRole === 'administrator';

  // const activeMaintenance = readMaintenanceState();
  // const isMaintenanceActive = activeMaintenance.enabled && activeMaintenance.untilMs > Date.now();

  const year = new Date().getFullYear();
  const buildNumber = import.meta.env.VITE_DOC_BUILD_NUMBER || 'N/A';

  const getRoleMeta = (role: string) => {
    const definition = roleDefinitions.find(
      candidate => normalizeRoleName(candidate.name) === normalizeRoleName(role),
    );

    return {
      title: definition?.name || role,
      subtitle: definition?.description?.trim() || 'ROLE PROFILE',
      iconType: 'Users',
      iconColor: definition?.roleTextColor || undefined,
    };
  };

  const getRolePriority = (role: string) => {
    return rolePriorities.get(normalizeRoleName(role)) ?? Number.MAX_SAFE_INTEGER;
  };

  const displayedRoles = useMemo(
    () =>
      Array.from(new Set([...(normalizedRoles ?? []), ...(loginCurrentRole ? [loginCurrentRole] : [])])).sort((a, b) =>
        getRolePriority(a) - getRolePriority(b) || a.localeCompare(b),
      ),
    [normalizedRoles, loginCurrentRole],
  );

  // const openMaintenanceModal = () => {
  //   if (isMaintenanceActive) {
  //     const remainingMs = Math.max(0, activeMaintenance.untilMs - Date.now());
  //     setMaintenanceMinutes(Math.max(1, Math.ceil(remainingMs / 60000)));
  //     setMaintenanceReason(activeMaintenance.message || '');
  //   }
  //   setIsMaintenanceModalOpen(true);
  // };

  // const showMaintenanceError = (message: string, endpoint?: string, status?: number) => {
  //   const details = endpoint && status ? ` (${endpoint} - HTTP ${status})` : '';
  //   notifications.show({ 
  //     color: 'red', 
  //     title: 'Maintenance log failed', 
  //     message: message + details,
  //     autoClose: 5000 
  //   });
  // };

  // Single maintenance endpoint handler - use one consistent API for frontend and backend
  // const postMaintenance = async (payload: { isMaintenanceEnabled: boolean; maintenanceTitle: string; maintenanceMessage: string; startTime: Date; endTime: Date; }): Promise<{ succeeded: boolean; message: string }> => {
  //   try {
  //     // Use the same relative path used across the app. `api` baseURL already contains the API root.
  //     const response = await api.post('v1/system-maintenance', payload);
  //     if (response?.data?.succeeded) {
  //       return { succeeded: true, message: 'Maintenance logged successfully.' };
  //     }
  //     const msg = response?.data?.message || 'Failed to save maintenance log.';
  //     showMaintenanceError(msg, 'v1/system-maintenance', response?.status);
  //     return { succeeded: false, message: msg };
  //   } catch (error: unknown) {
  //     const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string };
  //     const status = err?.response?.status ?? 0;
  //     const message = err?.response?.data?.message || err?.message || 'Network error';
  //     console.error('[Maintenance] POST v1/system-maintenance failed:', { status, message, error });
  //     showMaintenanceError(message, 'v1/system-maintenance', status);
  //     return { succeeded: false, message };
  //   }
  // };

  // const submitMaintenance = async () => {
  //   const safeMinutes = Math.max(1, Math.floor(maintenanceMinutes || 0));
  //   const reason = maintenanceReason.trim();

  //   if (!reason) {
  //     notifications.show({ color: 'red', title: 'Maintenance', message: 'Please enter maintenance reason.' });
  //     return;
  //   }

  //   setIsSubmitting(true);
  //   const now = new Date();
  //   const scheduledEndTime = new Date(Date.now() + safeMinutes * 60000);

  //   const payload = {
  //     isMaintenanceEnabled: true,
  //     maintenanceTitle: 'System Maintenance',
  //     maintenanceMessage: reason,
  //     startTime: now,
  //     endTime: scheduledEndTime,
  //   };

  //   try {
  //     const result = await postMaintenance(payload);
  //     if (!result.succeeded) {
  //       setIsSubmitting(false);
  //       return;
  //     }
  //     // Store maintenance state in localStorage and sync UI immediately
  //     writeMaintenanceState({ enabled: true, untilMs: scheduledEndTime.getTime(), message: reason });
  //     window.dispatchEvent(new StorageEvent('storage', { key: 'dc_maintenance_state_v1' }));
  //   } catch (error) {
  //     console.error('Error recording maintenance to database:', error);
  //     showMaintenanceError('Failed to save maintenance log to database.');
  //     setIsSubmitting(false);
  //     return;
  //   }

  //   notifications.show({
  //     color: 'teal',
  //     title: 'Maintenance enabled',
  //     message: 'Website maintenance mode is active and recorded in database.',
  //     autoClose: 4000,
  //   });
  //   setIsSubmitting(false);
  //   setIsMaintenanceModalOpen(false);
  // };

  // const handleDisableMaintenance = async () => {
  //   const now = new Date();

  //   const payload = {
  //     isMaintenanceEnabled: false,
  //     maintenanceTitle: 'Maintenance',
  //     maintenanceMessage: activeMaintenance.message || '',
  //     startTime: now,
  //     endTime: now,
  //   };

  //   try {
  //     const result = await postMaintenance(payload);

  //     if (!result.succeeded) {
  //       showMaintenanceError(result.message);
  //     }
  //   } catch (error) {
  //     console.error('Error recording maintenance deactivation to database:', error);
  //     showMaintenanceError('Failed to save maintenance log to database.');
  //   }

  //   clearMaintenanceState();
  //   notifications.show({
  //     color: 'teal',
  //     title: 'Maintenance disabled',
  //     message: 'Website maintenance mode has been turned off and recorded in database.',
  //     autoClose: 4000,
  //   });
  // };

  return (
    <div>
      <Menu withArrow offset={6} arrowSize={10} trigger="hover" width={320} radius={18} shadow="lg">
        <Menu.Target>
          <Flex align={'center'} className={classes.cursor} gap={'xs'}>
            <Avatar color={clickableColor} radius="xl">
              {firstLetter}
            </Avatar>
            {isEnableIconOnly && (
              <div>
                <Text fw={700} color="#fff" fz={'h6'}>{fullName}</Text>
                <Text c={'#fff'} fz={'xs'}>
                  {loginCurrentRole}
                </Text>
              </div>
            )}
          </Flex>
        </Menu.Target>
        <Menu.Dropdown p={14} style={{ borderRadius: 18, border: '1px solid #dce6f5' }}>
          {/* {canAccessMyProfile && (
            <>
              <Menu.Item
                leftSection={<DisplayIcon iconName="user" />}
                onClick={() => navigate(ROUTES.people.profile)}
              >
                My Profile
              </Menu.Item>
              <Menu.Divider my={12} />
            </>
          )} */}
          <Group justify={'space-between'} mb={10} px={4}>
            <Text fz={10} fw={800} c={'#94a3b8'} tt={'uppercase'} lts={1}>
              Switch Role
            </Text>
            {/* {canManageMaintenance ? (
              <Button size="compact-xs" variant="light" onClick={openMaintenanceModal}>
                {isMaintenanceActive ? 'Update Maintenance' : 'Maintenance Off'}
              </Button>
            ) : null} */}
          </Group>

          {/* {canManageMaintenance ? (
            <Group justify={'space-between'} mb={12} px={4}>
              <Badge color={isMaintenanceActive ? 'orange' : 'gray'} variant="light">
                {isMaintenanceActive ? 'Site under maintenance' : 'Site live'}
              </Badge>
              {isMaintenanceActive ? (
                <Button size="compact-xs" color="red" variant="subtle" onClick={handleDisableMaintenance}>
                  Disable
                </Button>
              ) : null}
            </Group>
          ) : null} */}

          <Flex direction={'column'} gap={8}>
            {displayedRoles.map((role: string) => {
              const selected = loginCurrentRole === role;
              const roleMeta = getRoleMeta(role);
              const isHovered = hoveredRole === role;

              return (
                <Box
                  key={role}
                  onClick={() => switchRole(role)}
                  onMouseEnter={() => setHoveredRole(role)}
                  onMouseLeave={() => setHoveredRole(null)}
                  style={{
                    borderRadius: 14,
                    padding: '11px 12px',
                    cursor: 'pointer',
                    border: selected ? '1px solid #cfe2ff' : '1px solid #eef2f7',
                    background: selected ? 'linear-gradient(180deg, #f6faff 0%, #eef6ff 100%)' : '#ffffff',
                    boxShadow: selected ? '0 8px 16px rgba(37, 99, 235, 0.14)' : isHovered ? '0 4px 10px rgba(15, 23, 42, 0.08)' : 'none',
                    transition: 'all 0.18s ease',
                  }}
                >
                  <Flex align={'center'} justify={'space-between'}>
                    <Flex align={'center'} gap={12}>
                      {/* <Flex
                        align={'center'}
                        justify={'center'}
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          flexShrink: 0,
                          backgroundColor: selected || isHovered ? '#72d1cc' : '#f1f5f9',
                          boxShadow: selected || isHovered ? '0 8px 16px rgba(37, 99, 235, 0.22)' : 'none',
                          transition: 'all 0.18s ease',
                        }}
                      >
                        <DisplayIcon
                          iconName={roleMeta.iconType}
                          iconColor={roleMeta.iconColor}
                        />
                      </Flex> */}
                      <Flex direction={'column'} gap={1}>
                        <Text fz={12} fw={800} c={'#334155'} lh={'16px'}>
                          {roleMeta.title}
                        </Text>
                        <Text fz={10} fw={700} c={'#9fb0c8'} tt={'uppercase'} lts={0.85} lh={'12px'}>
                          {roleMeta.subtitle}
                        </Text>
                      </Flex>
                    </Flex>
                    {selected ? (
                      <DisplayIcon iconName="Check" width={17} height={17} />
                    ) : (
                      <Box w={17} h={17} />
                    )}
                  </Flex>
                </Box>
              );
            })}
          </Flex>

          <Menu.Divider my={12} />

          <Flex align={'center'} gap={10} px={12} py={10} style={{ borderRadius: 12, cursor: 'pointer' }} onClick={logoutuser}>
            <DisplayIcon iconName={'logout'} />
            <Text fz={14} fw={700} c={'#fb7185'}>
              Logout
            </Text>
          </Flex>

          <Menu.Divider my={12} />

          <Flex align={'center'} justify={'space-between'} px={6} pb={2}>
            <Anchor c={clickableColor} fz={11} href="https://pinion.services/company/privacy" target="_blank" className={classes.fontStyle}>
                Legal & Privacy
            </Anchor>

            <Flex align={'center'} justify={'end'} gap={6}>
              <Text fz={11} className={classes.fontStyle} c={'#94a3b8'}>
                Release: {buildNumber || year}
              </Text>
              <Text fz={11} c={'#94a3b8'}>
                (c) Pinion
              </Text>
            </Flex>
          </Flex>
        </Menu.Dropdown>
      </Menu>

      {/* <Modal
        opened={isMaintenanceModalOpen}
        onClose={() => !isSubmitting && setIsMaintenanceModalOpen(false)}
        title="System Maintenance"
        centered
        radius="md"
      >
        <Flex direction={'column'} gap={12}>
          <Text fz={12} c={'#64748b'}>
            Set maintenance duration and reason. Users will see maintenance page until the end time.
          </Text>
          <NumberInput
            label="Duration (minutes)"
            min={1}
            max={1440}
            value={maintenanceMinutes}
            onChange={(value) => setMaintenanceMinutes(Number(value) || 0)}
            disabled={isSubmitting}
          />
          <Textarea
            label="Reason"
            minRows={3}
            value={maintenanceReason}
            onChange={(event) => setMaintenanceReason(event.currentTarget.value)}
            placeholder="Explain what update is in progress"
            disabled={isSubmitting}
          />
          <Group justify={'flex-end'}>
            <Button variant="default" onClick={() => setIsMaintenanceModalOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={submitMaintenance} loading={isSubmitting}>
              Enable maintenance
            </Button>
          </Group>
        </Flex>
      </Modal> */}
    </div>
  );
};
export default UserMenu;
